/**
 * PageContentContext
 *
 * Global provider that automatically fetches SEO content blocks
 * (intro, faqs, relatedBlogs, relatedCategories) for the current route.
 *
 * Works in two modes:
 *   1. Full-page load  — reads the __PAGE_CONTENT__ script tag that Django
 *      injects server-side. Zero extra network request. Googlebot-friendly.
 *   2. Client-side nav — calls /api/seo/page-data/ whenever location.pathname
 *      changes. Resets content between navigations.
 *
 * Usage (in any component anywhere in the tree):
 *   import { usePageContentContext } from '../context/PageContentContext';
 *   const content = usePageContentContext();
 *   // content = { h1, intro, faqs, relatedBlogs, relatedCategories, nearbySuburbs }
 *
 * Wrap once in App.js — all current and future pages automatically get content.
 * No per-page code required for new routes.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PageContentContext = createContext(null);

// One-time flag: the server-injected script tag is only valid on the very first
// render after a full page load. After that we always call the API.
let _scriptTagConsumed = false;

export function PageContentProvider({ children }) {
  const location = useLocation();
  const [content, setContent] = useState(null);

  useEffect(() => {
    // Reset every time the user navigates to a new route
    setContent(null);

    // --- Attempt 1: server-injected __PAGE_CONTENT__ tag (first load only) ---
    if (!_scriptTagConsumed) {
      const el = document.getElementById('__PAGE_CONTENT__');
      if (el) {
        try {
          const parsed = JSON.parse(el.textContent || '{}');
          // Accept both wrapper formats produced by Django:
          //   {content: {...}, _path: '/blog/'}       ← fixed format
          //   {h1: '...', intro: '...', faqs: [...]}  ← bare legacy format
          const c = parsed?.content
            ?? (parsed?.h1 || parsed?.intro || parsed?.faqs ? parsed : null);
          if (c && (c.h1 || c.intro || c.faqs?.length)) {
            _scriptTagConsumed = true;
            setContent(c);
            return;
          }
        } catch (e) { /* malformed JSON */ }
      }
      _scriptTagConsumed = true; // don't retry even if tag was empty/missing
    }

    // --- Attempt 2: API fallback ---
    const path = location.pathname;
    let cancelled = false;

    fetch(`/api/seo/page-data/?path=${encodeURIComponent(path)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data?.content && (data.content.h1 || data.content.intro || data.content.faqs?.length)) {
          setContent(data.content);
        }
      })
      .catch(() => {});

    // Cleanup: if the user navigates away before the fetch resolves, ignore it
    return () => { cancelled = true; };
  }, [location.pathname]);

  return (
    <PageContentContext.Provider value={content}>
      {children}
    </PageContentContext.Provider>
  );
}

/**
 * Hook — consume page content in any component.
 * Returns null while loading or when no content exists for this path.
 */
export function usePageContentContext() {
  return useContext(PageContentContext);
}
